import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { IMAGES, PHONE_TEL } from "../lib/content";

const HEADLINE = [
  "Your Trusted Same-Day",
  "Medical & Business",
  "Courier Across",
  "Metro Atlanta",
];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Metro Atlanta skyline" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-navy/40" />
      <div className="noise-overlay" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative mx-auto w-full max-w-7xl px-5 pb-28 pt-32 lg:px-8">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold backdrop-blur-sm"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-gold" /> Decatur, GA · Serving Metro Atlanta
        </motion.span>

        <h1 className="mt-7 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-7xl" data-testid="hero-headline">
          {HEADLINE.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                {line.includes("Metro Atlanta") ? (
                  <>Metro <span className="text-gold">Atlanta</span></>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-2.5 text-sm font-semibold text-gold backdrop-blur-sm"
          data-testid="hero-trust-badge"
        >
          <span>HIPAA Trained</span>
          <span className="text-gold/50">•</span>
          <span>Bloodborne Pathogen Certified</span>
          <span className="text-gold/50">•</span>
          <span>Serving Metro Atlanta</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 lg:text-lg"
          data-testid="hero-subheadline"
        >
          Wilson Courier Solutions provides secure, professional same-day courier services for hospitals,
          laboratories, pharmacies, medical practices, law firms, financial institutions, and businesses
          throughout Metro Atlanta.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-quote-btn"
            onClick={() => go("#contact")}
            className="group flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-base font-bold text-navy shadow-[0_12px_36px_rgba(201,162,39,0.4)] transition-[transform] duration-200 hover:scale-[1.03]"
          >
            Request a Quote
            <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="hero-call-btn"
            className="flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/15"
          >
            <Phone className="h-5 w-5" /> Call Now
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/15 pt-8 text-white/70"
        >
          {["Same-Day Delivery", "HIPAA Trained", "Locally Owned", "Professional Drivers"].map((t) => (
            <span key={t} className="text-sm font-medium">{t}</span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
